import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Switch
} from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import {
  Upload as UploadIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import http from '../../http-common';
import { clearCurrencyCache } from '../../utils/currencyUtils';
import { useAppTheme } from '../../contexts/AppThemeContext';
import { DEFAULT_THEME_CONFIG } from '../../theme/buildAppTheme';
import { useAuth } from '../../contexts/AuthContext';

const Settings = () => {
  const { t } = useTranslation();
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currency, setCurrency] = useState('USD');
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [themeMode, setThemeMode] = useState(DEFAULT_THEME_CONFIG.mode);
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_THEME_CONFIG.primaryColor);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_THEME_CONFIG.secondaryColor);
  const [savingTheme, setSavingTheme] = useState(false);
  const { saveThemeConfig, reloadTheme } = useAppTheme();
  const [clinic, setClinic] = useState({
    clinic_name: '',
    clinic_address: '',
    clinic_phone: '',
    clinic_email: '',
    clinic_nit: '',
    provider_name: '',
    provider_license: '',
  });
  const [savingClinic, setSavingClinic] = useState(false);
  const { hasRole, hasPermission } = useAuth();
  const [sender, setSender] = useState({
    from_name: '',
    from_email: '',
    from_verified: false,
  });
  const [savingSender, setSavingSender] = useState(false);

  const currencies = [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'GTQ', name: 'Guatemalan Quetzal', symbol: 'Q' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'MXN', name: 'Mexican Peso', symbol: '$' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: '$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: '$' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
    { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
    { code: 'ARS', name: 'Argentine Peso', symbol: '$' },
    { code: 'CLP', name: 'Chilean Peso', symbol: '$' },
    { code: 'COP', name: 'Colombian Peso', symbol: '$' },
    { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/' },
    { code: 'CRC', name: 'Costa Rican Colón', symbol: '₡' },
    { code: 'PAB', name: 'Panamanian Balboa', symbol: 'B/.' },
    { code: 'HNL', name: 'Honduran Lempira', symbol: 'L' },
    { code: 'NIO', name: 'Nicaraguan Córdoba', symbol: 'C$' },
    { code: 'DOP', name: 'Dominican Peso', symbol: 'RD$' },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await http.get('/settings');
      if (response.data.logo_url) {
        setLogoPreview(response.data.logo_url);
      }
      if (response.data.settings?.currency) {
        setCurrency(response.data.settings.currency);
      }
      setThemeMode(response.data.settings?.theme_mode === 'dark' ? 'dark' : 'light');
      setPrimaryColor(response.data.settings?.primary_color || DEFAULT_THEME_CONFIG.primaryColor);
      setSecondaryColor(response.data.settings?.secondary_color || DEFAULT_THEME_CONFIG.secondaryColor);
      setClinic({
        clinic_name: response.data.clinic_name || '',
        clinic_address: response.data.clinic_address || '',
        clinic_phone: response.data.clinic_phone || '',
        clinic_email: response.data.clinic_email || '',
        clinic_nit: response.data.clinic_nit || '',
        provider_name: response.data.provider_name || '',
        provider_license: response.data.provider_license || '',
      });
      setSender({
        from_name: response.data.settings?.from_name || '',
        from_email: response.data.settings?.from_email || '',
        from_verified: !!response.data.settings?.from_verified,
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCurrencyChange = async (event) => {
    const newCurrency = event.target.value;
    setCurrency(newCurrency);
    
    try {
      setSavingCurrency(true);
      await http.put('/settings', { currency: newCurrency });
      clearCurrencyCache(); // Clear cache so new currency is loaded
      setMessage({ type: 'success', text: t('settings.currencyUpdatedSuccess') });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error updating currency:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || t('settings.failedUpdateCurrency') 
      });
      // Revert to previous currency
      setCurrency(currency);
    } finally {
      setSavingCurrency(false);
    }
  };

  const handleSaveTheme = async () => {
    try {
      setSavingTheme(true);
      await saveThemeConfig({
        mode: themeMode,
        primaryColor,
        secondaryColor,
      });
      await reloadTheme();
      setMessage({ type: 'success', text: t('settings.themeUpdatedSuccess') });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving theme:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || t('settings.failedUpdateTheme'),
      });
    } finally {
      setSavingTheme(false);
    }
  };

  const handleSaveClinic = async () => {
    try {
      setSavingClinic(true);
      await http.put('/settings', clinic);
      setMessage({ type: 'success', text: t('settings.clinicUpdatedSuccess') });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving clinic info:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || t('settings.failedUpdateClinic'),
      });
    } finally {
      setSavingClinic(false);
    }
  };

  const handleSaveSender = async () => {
    try {
      setSavingSender(true);
      const response = await http.put('/settings/email-sender', sender);
      setSender({
        from_name: response.data.from_name || '',
        from_email: response.data.from_email || '',
        from_verified: !!response.data.from_verified,
      });
      setMessage({ type: 'success', text: t('settings.senderUpdatedSuccess') });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving sender email:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || t('settings.failedUpdateSender'),
      });
    } finally {
      setSavingSender(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: t('settings.pleaseSelectImage') });
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setMessage({ type: 'error', text: t('settings.fileSizeMax') });
        return;
      }

      setLogo(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      setMessage({ type: '', text: '' });
    }
  };

  const handleUpload = async () => {
    if (!logo) {
      setMessage({ type: 'error', text: t('settings.pleaseSelectLogo') });
      return;
    }

    try {
      setUploading(true);
      setMessage({ type: '', text: '' });

      const formData = new FormData();
      formData.append('logo', logo);

      const response = await http.post('/settings/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setMessage({ type: 'success', text: t('settings.logoUploadedSuccess') });
      setLogoPreview(response.data.logo_url);
      setLogo(null);
    } catch (error) {
      console.error('Error uploading logo:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || t('settings.failedUploadLogo') 
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm(t('settings.confirmRemoveLogo'))) {
      return;
    }

    try {
      setUploading(true);
      await http.delete('/settings/logo');
      setLogoPreview(null);
      setLogo(null);
      setMessage({ type: 'success', text: t('settings.logoRemovedSuccess') });
    } catch (error) {
      console.error('Error removing logo:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || t('settings.failedRemoveLogo') 
      });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('nav.settings')}
      </Typography>
      
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">{t('settings.companyLogo')}</Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                {t('settings.companyLogoDescription')}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                {logoPreview && (
                  <Box
                    sx={{
                      width: 200,
                      height: 200,
                      border: '2px dashed #ccc',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      backgroundColor: '#f5f5f5'
                    }}
                  >
                    <img
                      src={logoPreview}
                      alt={t('settings.companyLogo')}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                )}

                <Box display="flex" gap={2} alignItems="center">
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="logo-file-input"
                    type="file"
                    onChange={handleFileSelect}
                  />
                  <label htmlFor="logo-file-input">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      disabled={uploading}
                    >
                      {t('settings.selectLogo')}
                    </Button>
                  </label>

                  {logo && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleUpload}
                      disabled={uploading}
                    >
                      {uploading ? <CircularProgress size={24} /> : t('settings.uploadLogo')}
                    </Button>
                  )}

                  {logoPreview && !logo && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRemoveLogo}
                      disabled={uploading}
                    >
                      {t('settings.removeLogo')}
                    </Button>
                  )}
                </Box>

                {logo && (
                  <Typography variant="caption" color="text.secondary">
                    {logo.name} ({(logo.size / 1024).toFixed(2)} KB)
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <MoneyIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">{t('settings.currencySettings')}</Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                {t('settings.currencyDescription')}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <FormControl fullWidth>
                <InputLabel>{t('settings.currency')}</InputLabel>
                <Select
                  value={currency}
                  onChange={handleCurrencyChange}
                  label={t('settings.currency')}
                  disabled={savingCurrency || loading}
                >
                  {currencies.map((curr) => (
                    <MenuItem key={curr.code} value={curr.code}>
                      {curr.symbol} {curr.name} ({curr.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {savingCurrency && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.savingCurrency')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PaletteIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">{t('settings.themeSettings')}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t('settings.themeDescription')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>{t('settings.themeMode')}</InputLabel>
                    <Select
                      value={themeMode}
                      label={t('settings.themeMode')}
                      onChange={(e) => setThemeMode(e.target.value)}
                      disabled={savingTheme || loading}
                    >
                      <MenuItem value="light">{t('settings.themeModeLight')}</MenuItem>
                      <MenuItem value="dark">{t('settings.themeModeDark')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="color"
                    label={t('settings.primaryColor')}
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    disabled={savingTheme || loading}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="color"
                    label={t('settings.secondaryColor')}
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    disabled={savingTheme || loading}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleSaveTheme}
                    disabled={savingTheme || loading}
                  >
                    {savingTheme ? t('settings.savingTheme') : t('settings.saveTheme')}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {hasPermission('settings.clinic.view') && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">{t('settings.clinicInfo')}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t('settings.clinicInfoDescription')}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('settings.clinicName')}
                    value={clinic.clinic_name}
                    onChange={(e) => setClinic((p) => ({ ...p, clinic_name: e.target.value }))}
                    disabled={savingClinic || loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('settings.clinicPhone')}
                    value={clinic.clinic_phone}
                    onChange={(e) => setClinic((p) => ({ ...p, clinic_phone: e.target.value }))}
                    disabled={savingClinic || loading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('settings.clinicAddress')}
                    value={clinic.clinic_address}
                    onChange={(e) => setClinic((p) => ({ ...p, clinic_address: e.target.value }))}
                    disabled={savingClinic || loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="email"
                    label={t('settings.clinicEmail')}
                    value={clinic.clinic_email}
                    onChange={(e) => setClinic((p) => ({ ...p, clinic_email: e.target.value }))}
                    disabled={savingClinic || loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('settings.clinicNit')}
                    value={clinic.clinic_nit}
                    onChange={(e) => setClinic((p) => ({ ...p, clinic_nit: e.target.value }))}
                    disabled={savingClinic || loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('settings.providerName')}
                    value={clinic.provider_name}
                    onChange={(e) => setClinic((p) => ({ ...p, provider_name: e.target.value }))}
                    disabled={savingClinic || loading}
                    helperText={t('settings.providerNameHint')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('settings.providerLicense')}
                    value={clinic.provider_license}
                    onChange={(e) => setClinic((p) => ({ ...p, provider_license: e.target.value }))}
                    disabled={savingClinic || loading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleSaveClinic}
                    disabled={savingClinic || loading}
                  >
                    {savingClinic ? t('common.saving') : t('settings.saveClinic')}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        )}

        {hasRole('superadmin') && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">{t('settings.senderEmail')}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t('settings.senderEmailDescription')}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('settings.senderName')}
                      value={sender.from_name}
                      onChange={(e) => setSender((p) => ({ ...p, from_name: e.target.value }))}
                      disabled={savingSender || loading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="email"
                      label={t('settings.senderEmailAddress')}
                      value={sender.from_email}
                      onChange={(e) => setSender((p) => ({ ...p, from_email: e.target.value }))}
                      disabled={savingSender || loading}
                      helperText={t('settings.senderEmailHint')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={sender.from_verified}
                          onChange={(e) => setSender((p) => ({ ...p, from_verified: e.target.checked }))}
                          disabled={savingSender || loading}
                        />
                      }
                      label={t('settings.senderVerified')}
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('settings.senderVerifiedHint')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={handleSaveSender}
                      disabled={savingSender || loading}
                    >
                      {savingSender ? t('common.saving') : t('settings.saveSender')}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Settings;

